import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import MsiDatagrid from '../custom/MsiDatagridAuto'
import Icon from '../icon'
import apiUrl from '../../configs/constant'

type Row = { id: string | number; [key: string]: any }
type Props = { rows: Row[] }

/**
 * Pengganti hanya untuk tabel Tab Cetak BKK.
 * Tidak mengubah sumber data/route PDF individual existing.
 */
export default function BkkBulkPrintTable({ rows = [] }: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const allIds = useMemo(
    () => Array.from(new Set(rows.filter(row => row?.id != null).map(row => String(row.id)))),
    [rows]
  )

  // Reset selection setelah pencarian atau refresh agar tidak mencetak ID dari filter sebelumnya.
  useEffect(() => { setSelectedIds([]) }, [rows])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedCount = selectedIds.length
  const allChecked = allIds.length > 0 && selectedCount === allIds.length

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds(prev => checked
      ? Array.from(new Set([...prev, id]))
      : prev.filter(value => value !== id)
    )
  }

  const print = (paper: 'a5' | 'a6') => {
    // Urutan dokumen mengikuti urutan hasil list-bkk, bukan urutan klik checkbox.
    const ids = allIds.filter(id => selectedSet.has(id))
    if (ids.length === 0 || ids.length > 60) return
    const query = encodeURIComponent(ids.join(','))
    window.open(`${apiUrl.baseUrl}/acc/mutasi-kas-bank/pdf-bulk/${paper}?ids=${query}`, '_blank', 'noopener,noreferrer')
  }

  const actions = useMemo(() => [{
    key: 'pilih',
    field: 'pilih',
    headerName: 'Pilih',
    minWidth: 75,
    width: 75,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }: { row: Row }) => (
      <Checkbox
        size="small"
        aria-label={`Pilih BKK ${row.id}`}
        checked={selectedSet.has(String(row.id))}
        onClick={event => event.stopPropagation()}
        onChange={(_, checked) => toggle(String(row.id), checked)}
      />
    ),
  }], [selectedSet])

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Icon icon="mdi:printer" />}
          disabled={selectedCount === 0 || selectedCount > 60} onClick={() => print('a5')}>
          Cetak BKK A5
        </Button>
        <Button variant="contained" color="secondary" startIcon={<Icon icon="mdi:printer" />}
          disabled={selectedCount === 0 || selectedCount > 60} onClick={() => print('a6')}>
          Cetak BKK A6
        </Button>
        <FormControlLabel
          label="Pilih semua hasil pencarian"
          control={<Checkbox disabled={allIds.length === 0} checked={allChecked}
            indeterminate={selectedCount > 0 && !allChecked}
            onChange={(_, checked) => setSelectedIds(checked ? allIds : [])} />}
        />
        <Typography variant="body2">{selectedCount} BKK dipilih</Typography>
      </Stack>
      {selectedCount > 60 && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          Maksimal 60 BKK per cetakan. Kurangi jumlah checklist.
        </Typography>
      )}
      <MsiDatagrid
        array={rows}
        convertNumeric={['total']}
        hiddenColumns={['accprofitcenter_id', 'wo_order_id', 'doc_id']}
        actions={actions}
      />
    </Box>
  )
}
